<template>
  <div>
    <div class="fixed pin modal-bg"></div>
    <div class="fixed pin overflow-auto modal-scroll p-6">
      <div class="modal bg-white max-w-xl mx-auto p-6 rounded relative" :class="{'editor-open': editor.productId !== null}">
        <div class="rounded-full bg-black text-white absolute close-btn text-xl text-center border border-white cursor-pointer" title="Close" @click="closeModal()">
          &times;
        </div>

        <div v-if="editor.productId === null">
          <h1 class="capitalize mb-6">{{ department }} Templates - {{ subscription.label }}</h1>

          <loader v-if="loadingTemplates" />
          <div v-else-if="templateError !== null">
            <p class="mb-3 text-sm"><i class="fa fa-exclamation-triangle"></i> {{ templateError }}</p>
          </div>
          <div v-else>
            <div class="bg-tertiary text-white p-3 mb-3 relative" v-if="!alreadyEnabled">
              <i class="fas fa-info-circle"></i> Select a template to enable this subscription.

              <div class="bg-green text-white p-3 absolute pin whitespace-no-wrap overflow-hidden enabled-alert" v-if="subscription.enabled">
                <i class="fas fa-check"></i> Subscription enabled.
              </div>
            </div>

            <div class="bg-red-dark text-white p-3 mb-3" v-if="outOfAreaEnabled && !subscription.out_of_area.product_id">
              <i class="fas fa-exclamation-triangle"></i> Out of area template not selected. Orders for out of area properties will not be created for this subscription.
            </div>

            <div v-if="outOfAreaEnabled" class="border-b mb-3">
              <input type="radio" id="groupAgent" v-model="group" value="agent" />
              <label for="groupAgent" class="tab border py-2 px-4 mr-3 rounded-tl rounded-tr uppercase" :class="group === 'agent' ? 'border-b-white text-black' : 'text-grey bg-grey-lighter'">
                In Area Templates
                <i class="fas fa-exclamation-triangle text-red-dark" v-if="subscription.product_id === null"></i>
              </label>

              <input type="radio" id="groupWhitelabel" v-model="group" value="whitelabel" />
              <label for="groupWhitelabel" class="tab border py-2 px-4 mr-3 rounded-tl rounded-tr uppercase" :class="group === 'whitelabel' ? 'border-b-white text-black' : 'text-grey bg-grey-lighter'">
                Out of Area Templates
                <i class="fas fa-exclamation-triangle text-red-dark" v-if="subscription.out_of_area.product_id === null"></i>
              </label>
            </div>

            <div class="border-b mb-6">
              <input type="radio" id="typeletter" v-model="type" value="letter" />
              <label for="typeletter" class="tab border py-2 px-4 mr-3 rounded-tl rounded-tr uppercase" :class="type === 'letter' ? 'border-b-white text-black' : 'text-grey bg-grey-lighter'">
                Letters
              </label>

              <input type="radio" id="typeCards" v-model="type" value="card" />
              <label for="typeCards" class="tab border py-2 px-4 mr-3 rounded-tl rounded-tr uppercase" :class="type === 'card' ? 'border-b-white text-black' : 'text-grey bg-grey-lighter'">
                Cards
              </label>

              <input type="radio" id="typeCardsEn" v-model="type" value="card_envelope" />
              <label for="typeCardsEn" class="tab border py-2 px-4 mr-3 rounded-tl rounded-tr uppercase" :class="type === 'card_envelope' ? 'border-b-white text-black' : 'text-grey bg-grey-lighter'">
                Cards (Enveloped)
              </label>
            </div>

            <div class="-mx-2 flex flex-wrap" :class="'templates-'+type" v-if="localTemplates.length > 0">
              <div v-for="(template, i) in localTemplates" :key="i" class="mb-6 px-2 template-frame flex flex-wrap" :title="'Template id: '+ template.product_id">
                <div class="w-full mb-3 flex">
                  <div class="w-full">
                    <input type="radio" class="green" :id="'template'+template.product_id" v-model="subscription.product_id" :value="template.product_id" @change="changeTemplate(false, template)" v-if="group === 'agent'">
                    <input type="radio" class="green" :id="'template'+template.product_id" v-model="subscription.out_of_area.product_id" :value="template.product_id" @change="changeTemplate(true, template)" v-else>
                    <label :for="'template'+template.product_id" class="uppercase">{{ template.label }}</label>
                    <div class="mt-1 text-xs text-grey uppercase" v-if="template.custom">
                      (Custom)
                    </div>
                  </div>
                  <i class="fas fa-pencil-alt text-tertiary text-lg cursor-pointer ml-2" title="Edit Template" @click="editTemplate(template)"></i>
                </div>
                <iframe class="border template-preview self-end" :srcdoc="template.html" :ref="`templateFrame${template.product_id}`" ></iframe>
              </div>
            </div>

            <div class="bg-grey-lighter text-grey-darkest p-3 mb-3" v-else>
              <i class="fas fa-info-circle"></i> There are no templates of this type.
            </div>
          </div>

          <iframe ref="templateFrame" class="template-iframe" />

          <paginator v-if="!loadingTemplates" :data="templates" @change="pageChanged"/>

        </div>

        <div v-else>
          <h1 class="capitalize mb-6">Template Editor - {{ editor.name }}</h1>
          <div class="overflow-hidden">
            <loader v-if="!editor.loaded" />
            <iframe :src="editor.url" class="editor overflow-hidden" @load="editor.loaded = true" :class="{'h-0': !editor.loaded}"></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { propertyLogicApi as api } from '@/http'
import srcDoc from 'srcdoc-polyfill'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'apsTemplateModal',
  props: {
    subscription: Object,
    department: String,
    outOfAreaEnabled: Boolean
  },
  computed: {
    ...mapState(['selectedOffice']),
    localTemplates: function () {
      let output = []

      if (!this.templates) {
        return output
      }

      for (let i = 0; i < this.templates.data.length; i++) {
        output.push({
          product_id: this.templates.data[i].product_id,
          extent: this.templates.data[i].maxSort,
          custom: this.templates.data[i].custom,
          label: this.templates.data[i].productDescription,
          html: `<!DOCTYPE HTML><html><head><style>html,body{padding:0;margin:0;overflow:hidden}${this.templates.data[i].css}</style></head><body>${this.templates.data[i].markup}</body></html>`
        })

        /*eslint-disable */
        setTimeout(() => {
          srcDoc.set(this.$refs[`templateFrame${this.templates.data[i].product_id}`][0])
        }, 100)
        /* eslint-enable */
      }

      return output
    }
  },
  data () {
    return {
      group: 'agent',
      type: 'letter',
      alreadyEnabled: false,
      loadingTemplates: true,
      templates: null,
      templateError: false,
      page: 1,
      editor: {
        productId: null,
        name: null,
        url: null,
        custom: false,
        loaded: false
      }
    }
  },
  created: function () {
    window.addEventListener('message', this.receiveMessageFromEditor)

    this.alreadyEnabled = this.subscription.enabled === true
    document.body.style.overflow = 'hidden'

    this.getTemplates()
  },
  beforeDestroy: function () {
    document.body.style.overflow = 'auto'

    window.removeEventListener('message', this.receiveMessageFromEditor)
  },
  methods: {
    ...mapActions(['displayToastNotification', 'setPageLoading']),
    getTemplates () {
      this.loadingTemplates = true
      this.setPageLoading(true)
      this.templateError = null
      return api.get(`/aps/triggers/templates/${this.selectedOffice.id}/${this.type}/${this.subscription.type_id}/${this.department}/${this.group === 'agent' ? '0' : '1'}`, {
        params: {
          page: this.page
        }
      }).then(res => {
        this.loadingTemplates = false
        this.setPageLoading(false)
        if (!res) {
          this.templateError = 'Failed to retrieve templates'
          return
        }

        this.templates = res.data
      }, () => {
        this.loadingTemplates = false
        this.setPageLoading(false)
        this.templateError = 'Failed to retrieve templates'
      })
    },
    changeTemplate: function (outOfArea, template) {
      if (outOfArea) {
        this.subscription.out_of_area.envelope = this.type !== 'card'
        this.subscription.out_of_area.letter = this.type === 'letter'
        this.subscription.out_of_area.extent = template.extent
        return
      }
      this.subscription.envelope = this.type !== 'card'
      this.subscription.letter = this.type === 'letter'
      this.subscription.extent = template.extent
    },
    editTemplate: function (template) {
      this.editor.loaded = false
      api.get(`/aps/triggers/editor/${this.selectedOffice.id}/${template.product_id}/${template.custom ? 1 : 0}`).then((response) => {
        this.editor.url = response.data.url
        this.editor.productId = template.product_id
        this.editor.name = template.label
        this.editor.custom = template.custom
      })
    },
    closeModal: function () {
      this.setPageLoading(false)
      if (this.editor.productId !== null) {
        if (confirm('Please make sure you have saved all template pages. Are you sure you want to close the template editor?')) {
          this.editor.productId = null
          this.getTemplates()
        }
      } else {
        this.$emit('close-modal')
      }
    },
    receiveMessageFromEditor (e) {
      if (e.data === 'close-editor') {
        this.editor.productId = null
        this.getTemplates()
      }
    },
    pageChanged (i) {
      this.page = i
      this.getTemplates()
    }
  },
  watch: {
    subscription: {
      handler (val) {
        this.subscription.enabled = this.subscription.product_id !== null
      },
      deep: true
    },
    group () {
      this.getTemplates()
    },
    type () {
      this.page = 1
      this.getTemplates()
    }
  }
}
</script>

<style lang="scss" scoped>
  .template-iframe {
    width: 250px;
    height: 0px;
  }
  .close-btn {
    width: 28px;
    height: 28px;
    top: -14px;
    right: -14px;
  }
  .modal-bg {
    background: rgba(0,0,0,.75);
    z-index: 300;
  }
  .modal-scroll {
    z-index: 301;
    .modal {
      animation: modal-intro .2s ease-out;
      transition: width 1s;

      &.editor-open {
        width: 1200px;
        max-width: none;
      }
    }
  }
  @keyframes modal-intro {
    from {
      opacity: 0;
      transform: translateY(50px)
    }
    to {
      opacity: 1;
      transform: translateY(0px)
    }
  }
  .tab {
    margin-bottom: -1px;
    z-index: 1;
    &::before, &::after {
      display: none !important;
    }
  }
  .border-b-white {
    border-bottom-color: #FFF;
  }
  .template-frame {
    width: 260px;
  }
  .template-preview {
    width: 243px;
    height: 173px;
    pointer-events: none;
  }
  .editor {
    width: 1200px;
    height: 1500px;
    transition: height 1s;

    &.h-0 {
      height: 0px;
    }
  }
  .templates-letter iframe {
    height: 346px;
  }
  label {
    white-space: normal !important;
  }
  .enabled-alert {
    animation: enabled-alert .4s ease-out;
  }
  @keyframes enabled-alert {
    from {
      width: 0%;
    }
    to {
      width: 100%;
    }
  }
</style>
